
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuthActions } from '@/hooks/useAuthActions';
import { useAuthInitialization } from '@/hooks/useAuthInitialization';
import { UserProfile, AuthContextType } from '@/types/auth';

// Création du contexte avec des valeurs par défaut
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Fournisseur du contexte d'authentification
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Utilise le hook personnalisé pour charger le profil utilisateur
  const { loadUserProfile } = useUserProfile();
  
  // Fonction pour rafraîchir manuellement le profil
  const refreshProfile = useCallback(async () => {
    if (!user) return null;
    
    try {
      console.log("Rafraîchissement du profil utilisateur...");
      const profile = await loadUserProfile(user.id);
      if (profile) {
        setUserProfile(profile);
      }
      return profile;
    } catch (err) {
      console.error("Erreur lors du rafraîchissement du profil:", err);
      return null;
    }
  }, [user, loadUserProfile]);
  
  // Utilise le hook personnalisé pour les actions d'authentification
  const { signIn, signOut, refreshSession } = useAuthActions({
    setUser,
    setSession,
    setUserProfile,
    setLoading,
    loadUserProfile
  });

  // Utilise le hook personnalisé pour l'initialisation de l'authentification
  useAuthInitialization({
    setUser,
    setSession,
    setUserProfile,
    setLoading,
    navigate,
    location,
    refreshSession
  });

  // Valeur du contexte
  const contextValue: AuthContextType = {
    user,
    session,
    userProfile,
    loading,
    refreshProfile,
    refreshSession,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
